import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Add as AddIcon, Menu as MenuIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Hidden,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDebounce } from "use-debounce";
import { getRoleBundleMappings, getRoleBundles, getSkillData } from "../../redux/actions/Role";
import { AppState } from "../../redux/store";
import { RoleState } from "../../types/models/RoleState";
import { toggleNavCollapsed } from "../../redux/actions";
import { fontColor } from "../../constants/ColorSets";
import { isCurrentAccountAdmin } from "../../components/AppBase/AppAuthProvider";
import AppLoader from "../../components/AppBase/AppLoader";
import AppInfoView from "../../components/AppBase/AppInfoView";
import { RoleBundleMapping } from "../../types/models/LTOSetup";
import { LTOSetupList } from "./LTOSetupList";
import LTOSetupDialog from "./LTOSetupDialog";

const LTOSetupPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isScreenLessThanSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState<boolean>(false);
  const [editLTOSetup, setEditLTOSetup] = useState<RoleBundleMapping>();
  const [openLTOSetupDialog, setOpenLTOSetupDialog] = useState<boolean>(false);
  const [displayLTOSetups, setDisplayLTOSetups] = useState<RoleBundleMapping[]>();
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText] = useDebounce(searchText, 700);

  const roleState = useSelector<AppState, RoleState>((state) => state.role);

  useEffect(() => {
    // Validate permission
    const isAdmin = isCurrentAccountAdmin();
    if (!isAdmin) {
      navigate(-1);
    }

    setLoading(true);
    dispatch(
      getRoleBundles(() => {
        dispatch(
          getSkillData(() => {
            dispatch(
              getRoleBundleMappings(() => {
                setLoading(false);
              })
            );
          })
        );
      })
    );
  }, []);

  useEffect(() => {
    if (debouncedSearchText) {
      const filteredSetups = roleState.roleBundleMappings?.filter((setup) =>
        setup.Name.toLowerCase().includes(debouncedSearchText.toLowerCase())
      );
      setDisplayLTOSetups(filteredSetups);
    } else {
      setDisplayLTOSetups(roleState.roleBundleMappings);
    }
  }, [roleState.roleBundleMappings, debouncedSearchText]);

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            width: "95%",
            height: "95%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AppLoader />
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: theme.page.bgColor,
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "1000px", p: 2 }}>
          <Box>
            <Box
              sx={{
                minHeight: "60px",
                width: "100%",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <Hidden lgUp>
                  <IconButton
                    sx={{
                      color: "text.secondary",
                      marginLeft: 0,
                      padding: 0,
                      marginRight: 1.5,
                    }}
                    edge="start"
                    className="menu-btn"
                    color="inherit"
                    aria-label="open drawer"
                    onClick={() => dispatch(toggleNavCollapsed())}
                    size="large"
                  >
                    <MenuIcon
                      sx={{
                        width: 35,
                        height: 35,
                      }}
                    />
                  </IconButton>
                </Hidden>
                <Box sx={{ py: 1 }}>
                  <Typography
                    variant="h3"
                    component="h3"
                    color={fontColor.titleFontColor}
                    style={{ fontSize: 24 }}
                  >
                    LTO / Role Bundle Setup
                  </Typography>
                </Box>
                <Box sx={{ ml: 2 }}>
                  <Button
                    startIcon={<AddIcon />}
                    disabled={loading}
                    variant="contained"
                    onClick={() => {
                      setEditLTOSetup(undefined);
                      setOpenLTOSetupDialog(true);
                    }}
                    sx={{
                      width: isScreenLessThanSmall ? "90px" : "150px",
                      textTransform: "none",
                      borderRadius: 1,
                      color: "white",
                    }}
                  >
                    {isScreenLessThanSmall ? "Create " : "Create Setup"}
                  </Button>
                </Box>
              </Box>
              <Box>
                <TextField
                  id="search"
                  type="search"
                  label="Search"
                  variant="standard"
                  value={searchText}
                  sx={{ width: isScreenLessThanSmall ? 100 : 200, mb: 1, ml: 1 }}
                  onChange={(event) => {
                    setSearchText(event.target.value);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end", mb: 0.5, pr: 1 }}>
            <Typography color={"dimgrey"}>Total Setup: {displayLTOSetups?.length ?? 0}</Typography>
          </Box>
          <Box
            sx={{
              p: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <LTOSetupList
              isLoading={loading}
              onEditLTOSetup={(setup) => {
                setEditLTOSetup(setup);
                setOpenLTOSetupDialog(true);
              }}
              LTOSetups={displayLTOSetups}
            ></LTOSetupList>
          </Box>
        </Box>
      </Box>
      <LTOSetupDialog
        LTOSetup={editLTOSetup}
        isOpen={openLTOSetupDialog}
        onClose={() => setOpenLTOSetupDialog(false)}
      />
      <AppInfoView />
    </>
  );
};

export default LTOSetupPage;
